import React, { useState } from 'react';
import { EditableText } from '../ui/EditableText';
import { useBrand } from '@/context/BrandContext';
import { Goal, Eye, Upload } from 'lucide-react';
import { ImageUploadModal } from '../image-upload';
import { BrandSectionLayout } from './BrandSectionLayout';

interface MissionVisionSectionProps {
  section: any;
  isEditing: boolean;
  isGlobalDarkMode: boolean;
  editingElement: string | null;
  setEditingElement: (element: string | null) => void;
  updateSectionContent: (key: string, value: string) => void;
  updateSectionStyles: (key: string, styles: any) => void;
  pageNumber?: number;
}

export const MissionVisionSection: React.FC<MissionVisionSectionProps> = ({
  section,
  isEditing,
  isGlobalDarkMode,
  editingElement,
  setEditingElement,
  updateSectionContent,
  updateSectionStyles,
  pageNumber = 1
}) => {
  const { state } = useBrand();
  const [showImageModal, setShowImageModal] = useState(false);

  // Get primary color from brand data for icon backgrounds
  const primaryColor = state.brandData?.colors?.primary || '#3B82F6';

  const handleImageSelect = (imageUrl: string) => {
    updateSectionContent('missionVisionImage', imageUrl);
    setShowImageModal(false);
  };

  return (
    <BrandSectionLayout
      section={section}
      isEditing={isEditing}
      isGlobalDarkMode={isGlobalDarkMode}
      editingElement={editingElement}
      setEditingElement={setEditingElement}
      updateSectionContent={updateSectionContent}
      updateSectionStyles={updateSectionStyles}
      pageNumber={pageNumber}
    >
      {/* One column section with vertical stacking */}
      <div className="h-full flex flex-col gap-16">

        {/* Top - Our Mission & Vision title */}
        <div className="w-full">
          <EditableText
            value={section.content.sectionTitle || 'Mission & Vision'}
            onChange={(value) => updateSectionContent('sectionTitle', value)}
            element="h3"
            className="text-3xl font-bold leading-tight"
            placeholder="Mission & Vision"
            editModeEnabled={isEditing}
            isEditing={isEditing && editingElement === 'sectionTitle'}
            onEditStart={() => setEditingElement('sectionTitle')}
            onEditEnd={() => setEditingElement(null)}
            onStyleChange={(styles) => updateSectionStyles('sectionTitle', styles)}
            initialStyles={{
              textTransform: 'capitalize',
              fontWeight: 'bold',
              lineHeight: 1.2,
              fontFamily: state.brandData?.globalTitleFont || 'Inter',
              ...section.content.sectionTitleStyles
            }}
            isGlobalDarkMode={isGlobalDarkMode}
            sectionBackgroundColor={section.customization?.backgroundColor || section.styles.backgroundColor}
          />
        </div>

        {/* Grid with three columns */}
        <div className="flex-1 flex items-center justify-center">
          <div className="grid grid-cols-3 gap-8 w-full h-full">

            {/* Left card - Mission - medium border radius, extra soft shadow, full height and width */}
            <div
              className="w-full h-full p-6 flex flex-col gap-4 rounded-md shadow-sm bg-white"
              style={{
                padding: '24px',
                borderRadius: '0.375rem',
                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)'
              }}
            >
              {/* Icon and Title on same line */}
              <div className="flex items-center gap-3 mb-4">
                {/* Icon with primary color background and light fill - Goal icon */}
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: primaryColor }}
                >
                  <Goal className="w-6 h-6 text-white" />
                </div>

                {/* H6 Text "Our Mission" Capitalize, font-bold, leading-tight */}
                <EditableText
                  value={section.content.missionTitle || 'Our Mission'}
                  onChange={(value) => updateSectionContent('missionTitle', value)}
                  element="h6"
                  className="text-xl font-bold leading-tight"
                  placeholder="Our Mission"
                  editModeEnabled={isEditing}
                  isEditing={isEditing && editingElement === 'missionTitle'}
                  onEditStart={() => setEditingElement('missionTitle')}
                  onEditEnd={() => setEditingElement(null)}
                  onStyleChange={(styles) => updateSectionStyles('missionTitle', styles)}
                  initialStyles={{
                    textTransform: 'capitalize',
                    fontWeight: 'bold',
                    lineHeight: 1.2,
                    fontFamily: state.brandData?.globalTitleFont || 'Inter',
                    ...section.content.missionTitleStyles
                  }}
                  isGlobalDarkMode={isGlobalDarkMode}
                  sectionBackgroundColor="#FFFFFF"
                />
              </div>

              {/* P element text from onboarding form */}
              <EditableText
                value={section.content.mission || ''}
                onChange={(value) => updateSectionContent('mission', value)}
                element="p"
                className="text-base leading-relaxed"
                placeholder="Add Description"
                multiline={true}
                editModeEnabled={isEditing}
                isEditing={isEditing && editingElement === 'mission'}
                onEditStart={() => setEditingElement('mission')}
                onEditEnd={() => setEditingElement(null)}
                onStyleChange={(styles) => updateSectionStyles('mission', styles)}
                initialStyles={{
                  fontFamily: state.brandData?.globalTextFont || 'Inter',
                  ...section.content.missionStyles
                }}
                isGlobalDarkMode={isGlobalDarkMode}
                sectionBackgroundColor="#FFFFFF"
              />
            </div>

            {/* Middle card - Vision - medium border radius, extra soft shadow, full height and width, primary color background */}
            <div
              className="w-full h-full p-6 flex flex-col gap-4 rounded-md shadow-sm"
              style={{
                padding: '24px',
                backgroundColor: primaryColor,
                borderRadius: '0.375rem',
                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)'
              }}
            >
              {/* Icon and Title on same line */}
              <div className="flex items-center gap-3 mb-4">
                {/* Icon with light background and primary color fill - Eye icon */}
                <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center flex-shrink-0">
                  <Eye
                    className="w-6 h-6"
                    style={{ color: primaryColor }}
                  />
                </div>

                {/* H6 Text "Our Vision" Capitalize, font-bold, leading-tight */}
                <EditableText
                  value={section.content.visionTitle || 'Our Vision'}
                  onChange={(value) => updateSectionContent('visionTitle', value)}
                  element="h6"
                  className="text-xl font-bold leading-tight text-white"
                  placeholder="Our Vision"
                  multiline={true}
                  editModeEnabled={isEditing}
                  isEditing={isEditing && editingElement === 'visionTitle'}
                  onEditStart={() => setEditingElement('visionTitle')}
                  onEditEnd={() => setEditingElement(null)}
                  onStyleChange={(styles) => updateSectionStyles('visionTitle', styles)}
                  initialStyles={{
                    textTransform: 'capitalize',
                    fontWeight: 'bold',
                    lineHeight: 1.2,
                    color: '#ffffff',
                    fontFamily: state.brandData?.globalTitleFont || 'Inter',
                    ...section.content.visionTitleStyles
                  }}
                  isGlobalDarkMode={isGlobalDarkMode}
                  sectionBackgroundColor={primaryColor}
                />
              </div>

              {/* P element text from onboarding form */}
              <EditableText
                value={section.content.vision || ''}
                onChange={(value) => updateSectionContent('vision', value)}
                element="p"
                className="text-base leading-relaxed text-white opacity-90"
                placeholder="Add Description"
                multiline={true}
                editModeEnabled={isEditing}
                isEditing={isEditing && editingElement === 'vision'}
                onEditStart={() => setEditingElement('vision')}
                onEditEnd={() => setEditingElement(null)}
                onStyleChange={(styles) => updateSectionStyles('vision', styles)}
                initialStyles={{
                  color: '#ffffff',
                  opacity: 0.9,
                  fontFamily: state.brandData?.globalTextFont || 'Inter',
                  ...section.content.visionStyles
                }}
                isGlobalDarkMode={isGlobalDarkMode}
                sectionBackgroundColor={primaryColor}
              />
            </div>

            {/* Right column - Editable Image */}
            <div className="w-full h-full relative group cursor-pointer">
              <img
                src={section.content.missionVisionImage || '/img-placeholder-16-9.png'}
                alt="Mission & Vision Image"
                className="w-full h-full object-cover rounded-md shadow-sm"
                style={{
                  borderRadius: '0.375rem',
                  boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)'
                }}
                onError={(e) => {
                  e.currentTarget.src = '/img-placeholder-16-9.png';
                }}
              />

              {/* Upload Overlay - Only visible in edit mode */}
              {isEditing && (
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center rounded-md">
                  <div className="text-center text-white">
                    <Upload className="w-8 h-8 mx-auto mb-2" />
                    <p className="text-sm font-medium mb-2">Change Image</p>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowImageModal(true);
                      }}
                      className="px-4 py-2 bg-white text-gray-900 rounded-md text-sm font-medium hover:bg-gray-100 transition-colors"
                    >
                      Upload
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Image Upload Modal */}
      <ImageUploadModal
        isOpen={showImageModal}
        onClose={() => setShowImageModal(false)}
        onImageSelect={handleImageSelect}
        isGlobalDarkMode={isGlobalDarkMode}
        title="Upload Vision Image"
        acceptedFormats={['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg']}
        maxFileSize={10}
        showUnsplash={true}
        initialSearchQuery="business vision inspiration"
      />
    </BrandSectionLayout>
  );
};